/** 权限项 */
export interface MpPermission {
  id: number
  permCode: string
  permName: string
  permType: string
  parentCode?: string
}

/** 权限分组（分类 + 子功能） */
export interface MpPermissionGroup {
  code: string
  name: string
  categoryId: number
  options: { label: string; value: number }[]
}

/** 权限树节点 */
export interface MpPermissionTreeNode {
  id: number
  label: string
  disabled?: boolean
  children?: MpPermissionTreeNode[]
}

export function buildPermissionTree(
  list: MpPermission[],
  disabled = false
): MpPermissionTreeNode[] {
  const categories = (list || []).filter(p => p.permType === 'category')
  return categories.map(cat => ({
    id: Number(cat.id),
    label: cat.permName,
    disabled,
    children: (list || [])
      .filter(p => p.parentCode === cat.permCode)
      .map(p => ({
        id: Number(p.id),
        label: p.permName,
        disabled
      }))
  }))
}

export function buildPermissionGroups(list: MpPermission[]): MpPermissionGroup[] {
  const categories = (list || []).filter(p => p.permType === 'category')
  return categories.map(cat => {
    const features = (list || [])
      .filter(p => p.parentCode === cat.permCode)
      .map(p => ({ label: p.permName, value: Number(p.id) }))
    return {
      code: cat.permCode,
      name: cat.permName,
      categoryId: Number(cat.id),
      options: [{ label: '整类可见', value: Number(cat.id) }].concat(features)
    }
  })
}

export function getGroupKey(roleId: number, groupCode: string): string {
  return `${roleId}_${groupCode}`
}

/** 从角色已有权限初始化各分组选中项 */
export function initRoleGroupPermMap(
  roleList: any[],
  permissionList: MpPermission[]
): Record<string, number[]> {
  const map: Record<string, number[]> = {}
  const groups = buildPermissionGroups(permissionList)
  roleList.forEach(role => {
    const permIds = (role.permissionIds || []).map((id: any) => Number(id))
    groups.forEach(group => {
      const groupIds = group.options.map(opt => Number(opt.value))
      map[getGroupKey(role.id, group.code)] = permIds.filter(id => groupIds.indexOf(id) >= 0)
    })
  })
  return map
}

/** 合并各分组选中项为角色权限 ID 列表 */
export function mergeRolePermissionIds(
  roleId: number,
  groups: MpPermissionGroup[],
  roleGroupPermMap: Record<string, number[]>
): number[] {
  const merged: number[] = []
  groups.forEach(group => {
    const ids = roleGroupPermMap[getGroupKey(roleId, group.code)] || []
    ids.forEach(id => {
      const numId = Number(id)
      if (merged.indexOf(numId) < 0) merged.push(numId)
    })
  })
  return merged
}

/**
 * 整类可见联动：勾选整类全选子项、取消整类清空、子项与整类联动
 */
export function applyGroupPermChange(
  prev: number[],
  selected: number[],
  group: MpPermissionGroup
): number[] {
  const categoryId = group.categoryId
  const featureIds = group.options.slice(1).map(o => Number(o.value))
  const prevHasCat = prev.indexOf(categoryId) >= 0
  const nowHasCat = selected.indexOf(categoryId) >= 0

  if (!prevHasCat && nowHasCat) {
    return [categoryId].concat(featureIds)
  }
  if (prevHasCat && !nowHasCat) {
    return []
  }
  if (prevHasCat && nowHasCat) {
    const allFeaturesSelected = featureIds.every(id => selected.indexOf(id) >= 0)
    if (!allFeaturesSelected) {
      return selected.filter(id => id !== categoryId)
    }
    return selected
  }
  if (!nowHasCat && featureIds.length > 0 && featureIds.every(id => selected.indexOf(id) >= 0)) {
    return [categoryId].concat(featureIds)
  }
  return selected
}
